import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useNavigate, useLocation } from 'react-router-dom';
import { Layout, ConfigProvider, theme, Menu, message, Dropdown, Avatar, Space, Drawer, Button } from 'antd';
import { UserOutlined, LogoutOutlined, DownOutlined, MenuOutlined, ProjectOutlined, SearchOutlined, FormOutlined, VideoCameraOutlined, DollarOutlined, RobotOutlined } from '@ant-design/icons';
import zhCN from 'antd/locale/zh_CN';
import './App.css';
import Login from './pages/Login';
import DataSubmissionPage from './pages/DataSubmission';
import Projects from './pages/Projects';
import ProtocolProjects from './pages/ProtocolProjects';
import ProjectForm from './pages/ProjectForm';
import ProtocolProjectForm from './pages/ProtocolProjectForm';
import Devices from './pages/Devices';
import DeviceForm from './pages/DeviceForm';
import DeviceManagement from './pages/DeviceManagement';
import FormConfigManagement from './pages/FormConfigManagement';
import DynamicFormExample from './pages/DynamicFormExample';
import DynamicFormNav from './pages/DynamicFormNav';
import EzvizVideoManagement from './pages/EzvizVideoManagement';
import SettlementCenter from './pages/SettlementCenter';
import DeviceQuery from './pages/DeviceQuery';
import AgentChat from './pages/AgentChat';

const { Content, Header } = Layout;

// Protected route component
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  // Check if user is authenticated
  const isAuthenticated = localStorage.getItem('token') !== null;
  
  if (!isAuthenticated) {
    // Redirect to login if not authenticated
    return <Navigate to="/login" replace />;
  }
  
  return <>{children}</>;
};

// Protected route component for UID 7 only
const ProtectedRouteForUID7 = ({ children }: { children: React.ReactNode }) => {
  // Check if user is authenticated
  const isAuthenticated = localStorage.getItem('token') !== null;
  
  if (!isAuthenticated) {
    // Redirect to login if not authenticated
    return <Navigate to="/login" replace />;
  }
  
  // Check if user has UID 7
  let isUID7 = false;
  try {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const user = JSON.parse(userStr);
      isUID7 = user.uid === 7;
    }
  } catch (error) {
    console.error('Error parsing user data:', error);
  }
  
  if (!isUID7) {
    // Show message and redirect to projects page if not UID 7
    message.error('您没有权限访问动态表单系统，只有UID为7的用户才能访问');
    return <Navigate to="/" replace />;
  }
  
  return <>{children}</>;
};

// User menu component that uses useNavigate
const UserMenu = ({ userName, isUID7 }: { userName: string; isUID7: boolean }) => {
  const navigate = useNavigate();
  
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    message.success('退出登录成功');
    navigate('/login');
  };

  const userMenuItems = [
    {
      key: 'userInfo',
      label: (
        <div className="user-menu-info">
          <span>{userName}</span>
          {isUID7 && <span className="admin-badge">管理员</span>}
        </div>
      ),
      disabled: true,
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      danger: true,
      icon: <LogoutOutlined />,
      label: '退出登录',
      onClick: handleLogout,
    },
  ];

  return (
    <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
      <a className="user-menu-trigger" onClick={(e) => e.preventDefault()}>
        <Avatar style={{ backgroundColor: '#007AFF' }} icon={<UserOutlined />} />
        <span className="user-name-text">{userName}</span>
      </a>
    </Dropdown>
  );
};

type NavItem = { key: string; label: string; path: string; icon?: React.ReactNode; adminOnly?: boolean; localOnly?: boolean };

const isLocalAccess = ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);

const NAV_ITEMS: NavItem[] = [
  { key: 'projects', label: '协议列表', path: '/', icon: <ProjectOutlined /> },
  { key: 'device-query', label: '设备查询', path: '/device-query', icon: <SearchOutlined /> },
  { key: 'dynamic-form', label: '动态表单系统', path: '/form-config-management', adminOnly: true, icon: <FormOutlined /> },
  { key: 'ezviz', label: '萤石云管理', path: '/ezviz-video-management', icon: <VideoCameraOutlined /> },
  { key: 'settlement', label: '结算中心', path: '/settlement-center', icon: <DollarOutlined /> },
  { key: 'agent', label: '智能助手（Agent）', path: '/agent', icon: <RobotOutlined />, localOnly: true },
];

const getActiveNavKey = (pathname: string) => {
  if (pathname === '/device-query') return 'device-query';
  if (pathname.includes('dynamic-form') || pathname === '/device-management' || pathname === '/form-config-management') return 'dynamic-form';
  if (pathname === '/ezviz-video-management') return 'ezviz';
  if (pathname === '/settlement-center') return 'settlement';
  if (pathname === '/' || pathname === '/projects' || pathname.startsWith('/area-') || pathname.startsWith('/protocol-') || pathname.startsWith('/devices') || pathname.startsWith('/device-')) return 'projects';
  if (pathname === '/agent') return 'agent';
  return '';
};

const getVisibleNavItems = (isUID7: boolean) => NAV_ITEMS.filter(item => (!item.adminOnly || isUID7) && (!item.localOnly || isLocalAccess));

// Desktop navigation links
const NavLinks = ({ isUID7 }: { isUID7: boolean }) => {
  const location = useLocation();
  const activeKey = getActiveNavKey(location.pathname);

  return (
    <nav className="app-nav-desktop" aria-label="主导航">
      {getVisibleNavItems(isUID7).map(item => (
        <Link key={item.key} to={item.path} className={`app-nav-link${activeKey === item.key ? ' app-nav-link--active' : ''}`}>
          {item.icon && <span className="app-nav-icon">{item.icon}</span>}
          {item.label}
        </Link>
      ))}
    </nav>
  );
};

// Mobile drawer navigation
const MobileNav = ({ isUID7, open, onClose }: { isUID7: boolean; open: boolean; onClose: () => void }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const activeKey = getActiveNavKey(location.pathname);

  return (
    <Drawer title="菜单" placement="left" open={open} onClose={onClose} width={260} className="app-nav-mobile-drawer">
      <Menu
        mode="inline"
        selectedKeys={activeKey ? [activeKey] : []}
        items={getVisibleNavItems(isUID7).map(item => ({
          key: item.key,
          icon: item.icon,
          label: item.label,
          onClick: () => { navigate(item.path); onClose(); },
        }))}
      />
    </Drawer>
  );
};

// App layout component
const AppLayout = ({ children }: { children: React.ReactNode }) => {
  const [isUID7, setIsUID7] = useState(false);
  const [userName, setUserName] = useState('');
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  
  useEffect(() => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        setIsUID7(user.uid === 7);
        setUserName(user.realName || '用户');
      }
    } catch (error) {
      console.error('Error parsing user data:', error);
    }
  }, []);

  return (
    <Layout style={{ 
      minHeight: '100vh',
      height: 'auto',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <Header className="app-header">
        <div className="app-header-left">
          <Button
            type="text"
            icon={<MenuOutlined />}
            className="app-nav-mobile-trigger"
            onClick={() => setMobileNavOpen(true)}
            aria-label="打开菜单"
          />
          <Link to="/" className="app-header-brand">
            <img src="/logo.svg" alt="Logo" className="app-logo" />
            <span className="app-title">综合数据管理系统</span>
          </Link>
          <NavLinks isUID7={isUID7} />
        </div>
        <div className="app-header-right">
          <UserMenu userName={userName} isUID7={isUID7} />
        </div>
        <MobileNav isUID7={isUID7} open={mobileNavOpen} onClose={() => setMobileNavOpen(false)} />
      </Header>
      <Content style={{ 
        padding: '0', 
        background: '#f0f2f5',
        height: 'auto', /* Ensure it only takes the space it needs */
        flex: '1 0 auto' /* Allow it to grow but not shrink */
      }}>
        <div className="page-container">
          {children}
        </div>
      </Content>
    </Layout>
  );
};

function App() {
  // Check if user is logged in
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    // Check for token on component mount and when localStorage changes
    const checkAuth = () => {
      const token = localStorage.getItem('token');
      setIsLoggedIn(!!token);
    };

    // Initial check
    checkAuth();

    // Listen for storage events (when localStorage changes)
    window.addEventListener('storage', checkAuth);
    
    return () => {
      window.removeEventListener('storage', checkAuth);
    };
  }, []);

  return (
    <ConfigProvider
      locale={zhCN}
      theme={{
        algorithm: theme.defaultAlgorithm,
        token: {
          colorPrimary: '#1677ff',
          borderRadius: 6,
        },
      }}
    >
      <Router>
        <div className="App">
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route 
              path="/" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Projects />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/agent" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <AgentChat />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/device-query" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <DeviceQuery />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/dynamic-form-nav" 
              element={
                <ProtectedRouteForUID7>
                  <AppLayout>
                    <DynamicFormNav />
                  </AppLayout>
                </ProtectedRouteForUID7>
              } 
            />
            <Route 
              path="/projects" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Projects />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/area-create" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <ProjectForm mode="create" />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/area-edit/:id" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <ProjectForm mode="edit" />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/protocol-projects/:protocolId" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <ProtocolProjects />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/protocol-project-create/:protocolId" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <ProtocolProjectForm mode="create" />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/protocol-project-edit/:protocolId/:projectId" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <ProtocolProjectForm mode="edit" />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            {/* Device routes */}
            <Route 
              path="/devices/:projectId" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Devices />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/device-create/:projectId" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <DeviceForm mode="create" />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/device-edit/:projectId/:deviceId" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <DeviceForm mode="edit" />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/data-submission" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <DataSubmissionPage />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
            {/* 动态表单管理路由 */}
            <Route 
              path="/device-management" 
              element={
                <ProtectedRouteForUID7>
                  <AppLayout>
                    <DeviceManagement />
                  </AppLayout>
                </ProtectedRouteForUID7>
              } 
            />
            <Route 
              path="/form-config-management" 
              element={
                <ProtectedRouteForUID7>
                  <AppLayout>
                    <FormConfigManagement />
                  </AppLayout>
                </ProtectedRouteForUID7>
              } 
            />
            {/* 动态表单示例路由 */}
            <Route 
              path="/dynamic-form-example" 
              element={
                <ProtectedRouteForUID7>
                  <AppLayout>
                    <DynamicFormExample />
                  </AppLayout>
                </ProtectedRouteForUID7>
              } 
            />
            {/* Ezviz Cloud Video Management Route */}
            <Route 
              path="/ezviz-video-management" 
              element={
                  <AppLayout>
                    <EzvizVideoManagement />
                  </AppLayout>
              } 
            />
            {/* Settlement Center Route */}
            <Route 
              path="/settlement-center" 
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <SettlementCenter />
                  </AppLayout>
                </ProtectedRoute>
              } 
            />
          </Routes>
        </div>
      </Router>
    </ConfigProvider>
  );
}

export default App;
